// src/core/clockSync.ts
//
// Backend↔browser clock-offset estimator.
//
// THE PROBLEM. The chart places backend-stamped samples (live values carry
// SymbolValue.timestamp_ns; historian rows carry their own ts) onto a time grid.
// If that grid is anchored on the browser's Date.now() but the samples carry the
// *backend's* clock, any skew between the two machines shears every sample into
// the wrong slot — or into "the future". Skew is not hypothetical:
//   • Docker Desktop runs containers in a Linux VM whose clock drifts from the
//     macOS host, especially after the Mac sleeps (the VM wakes seconds–minutes
//     behind until it resyncs). The plugin's time.Now() is the VM clock; the
//     browser's is the host clock.
//   • An edge gateway in the field may simply have a wrong RTC / lagging NTP.
//
// THE RULE WE FOLLOW. Never assume two machines' wall clocks agree. We MEASURE
// the offset and correct, then do all chart time-axis math in the backend frame.
// (Durations/scroll rate use Date.now() deltas, which are skew-immune — only the
// absolute offset differs between clocks, and it cancels in a delta.)
//
// THE ESTIMATOR — minimum-delay, decaying high-water (NOT a windowed max).
//   raw = backendTs - localArrivalNow  =  trueOffset − inFlightDelay   (delay ≥ 0)
// so raw ≤ trueOffset always, and the LEAST-delayed (largest-raw) recent sample
// is the best estimate (NTP's minimum-delay principle). The earlier design took
// a MAX over a sliding 5 s window — but that SAWTOOTHS: when the freshest sample
// expires out of the window the estimate DROPS, then the next fresh sample snaps
// it back up, a few times every few seconds. The chart anchor chased that
// sawtooth → the "pause then jump left" stutter. Instead we keep a high-water
// mark that:
//   • RISES instantly to any fresher sample (raw > current → adopt — strictly a
//     better, lower-delay estimate), and
//   • DECAYS down slowly (DECAY_MS_PER_S) so a one-off lucky-fast sample can't
//     pin the estimate forever as the real clocks drift.
// No window, no expiry, no sawtooth — the estimate is smooth, so the applied
// offset (below) is smooth, so the scroll is smooth. Real NTP drift is
// <<DECAY_MS_PER_S, so the slow decay still tracks genuine clock movement.
export class ClockSync {
  private offsetMs = 0; // the ESTIMATE — decaying high-water of raw
  private seeded = false;
  private lastDecayWall = 0;
  // The estimate relaxes DOWN at this rate when no fresher sample arrives.
  // 2 ms/s dwarfs real clock drift (ppm → ms/hour) yet is imperceptible on the
  // chart; it only exists so a stale peak can't pin the offset indefinitely.
  private static readonly DECAY_MS_PER_S = 2;

  /** Feed a backend timestamp (ms) the instant it arrives. No-op for absent ts. */
  observe(backendTsMs: number): void {
    if (!(backendTsMs > 0)) return;
    const localNow = Date.now();
    const raw = backendTsMs - localNow;
    if (!this.seeded) {
      this.offsetMs = raw;
      this.seeded = true;
      this.lastDecayWall = localNow;
      return;
    }
    // Decay the high-water down by the wall time elapsed since the last sample…
    const dt = localNow - this.lastDecayWall;
    if (dt > 0) {
      this.offsetMs -= (ClockSync.DECAY_MS_PER_S * dt) / 1000;
      this.lastDecayWall = localNow;
    }
    // …then raise it to any fresher (lower-delay) sample. Monotone-ish → smooth.
    if (raw > this.offsetMs) this.offsetMs = raw;
  }

  /**
   * "Now" in the BACKEND frame. The applied offset is FROZEN — it does NOT
   * continuously track the estimate — so the time axis advances at EXACTLY
   * real-time rate (d(now)/d(wall) = 1). This is non-negotiable for a chart
   * whose x-axis is "the last 10 s of the real world": any continuous easing
   * of the offset makes the scroll speed up/slow down (now = Date.now() +
   * appliedMs, so a changing appliedMs adds/subtracts from the scroll rate),
   * which is what an earlier ease did — visibly wrong, time doesn't dilate.
   *
   * Instead the frozen offset is corrected only DISCRETELY, and only when the
   * estimate drifts outside a DEADBAND. Transit jitter (the estimate wobbling
   * by tens of ms) stays inside the band → the offset never moves → the scroll
   * is perfectly uniform. A genuine clock step (a different machine's NTP jump,
   * a long sleep on skewed clocks) crosses the band and applies as ONE small
   * step — a rare, one-time horizontal nudge, never a rate change. On the same
   * machine (browser + backend) the true offset is a stable ~−transit, so the
   * band is never crossed and the offset stays frozen forever.
   *
   * Use this — not Date.now() — for EVERY chart time-axis computation (grid
   * anchor, advance, history window bounds, AND the tooltip hit-test) so the
   * line and the cursor read the same clock.
   */
  private appliedMs: number | undefined;
  // The estimate may wander this far from the frozen applied offset before a
  // one-time correction snaps applied to it. Wider than transit jitter (tens
  // of ms) so normal operation never corrects; narrow enough that a real drift
  // is caught before the chart is visibly wrong.
  private static readonly DEADBAND_MS = 300;

  now(): number {
    if (this.appliedMs === undefined) {
      this.appliedMs = this.offsetMs; // seed once
    } else if (Math.abs(this.offsetMs - this.appliedMs) > ClockSync.DEADBAND_MS) {
      this.appliedMs = this.offsetMs; // rare discrete correction (genuine drift only)
    }
    // Otherwise FROZEN → constant 1:1 scroll rate.
    return Date.now() + this.appliedMs;
  }

  /** Current estimated (backend − browser) offset in ms. Exposed for diagnostics. */
  get offsetMs_(): number {
    return this.offsetMs;
  }
}
